import {
  getGuidNullable,
  NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {
  Component,
  ExpressionPath,
  Expressions,
  getSystem,
  matchExpression,
  Plugins,
  Type,
  Types,
} from '@packages/systems/wfdl';
import {isNodePojo} from '@packages/systems/core/nodepojo';
import {
  EElement,
  EList,
  Expression,
  ExpressionElement,
  TRecord,
} from '@packages/systems/wfdl-ast';
import {unbindReferences} from '@packages/systems/user-components/unbind-references';
import {constant, emptyArray} from '@packages/utilities/fp/utils';
import {
  isUserComponent,
  isUserComponentInst,
  getDescendantsFromExpression,
  getElementWithAncestors,
  getPageComponentFromComponentMap,
} from '@packages/utilities/core-elements';
import {isSlot} from '@packages/utilities/elementTypeUtils';
// Direct-file import to skip barrels that would re-enter platform mid-load.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  deriveNativeIdFromPath,
  getInnermostSymbolInstance,
  getRelativePath,
  isPathToInstanceDescendant,
  getTargetElementOrInstance,
} from '@packages/utilities/native-id-paths/NativeIdPathUtils';
import {weakMemo} from '@packages/utilities/memo';
import {
  findSlotDefaultElement,
  findSlotDefaultElementAcrossTrees,
  getSlotDefaultElements,
  getSlotDefaultExpression,
  getTargetedSlotFieldId,
  resolveSlotDefaultPathContext,
} from './SlotDefaultUtils';
import {
  parsePropertyLabel,
  createPropertyLabel,
  getDataTypeFromElement,
} from '@packages/systems/user-components/utils';
import {
  getName,
  getPluginName,
  namesEqual,
} from '@packages/systems/wfdl-Qualified';
import {getStyleBlockIds} from '@packages/systems/style/style-blocks';
import type {RelativePositionPlacementOptions} from '@packages/utilities/core-elements';
import {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import {
  getSlots,
  getComponentSlots,
  allowedComponentsPath,
  getSlotAllowedComponentIds,
  setSlotAllowedComponentIds,
  hasSlotRestrictions,
} from '@packages/utilities/platform-slots';

// Re-export slot getters from the leaf `platform-slots` package so existing
// consumers of `@packages/utilities/platform` continue to import them from
// here unchanged. New consumers should import directly from
// `@packages/utilities/platform-slots`.
export {
  getSlots,
  getComponentSlots,
  allowedComponentsPath,
  getSlotAllowedComponentIds,
  setSlotAllowedComponentIds,
  hasSlotRestrictions,
};

const childrenPath: ExpressionPath = [{in: 'Record', at: 'children'}];

/**
 * Collects the SitePlugin component ids authored as a SlotContent field's
 * default content. Reads both at-rest shapes -- `defaultData` on a migrated
 * component, the field's legacy `system.defaultValue` otherwise -- the same way
 * SlotDefaultUtils.ts's forEachSlotDefaultComponentNode does across all fields.
 *
 * Reading only one shape silently returns nothing for half the components in a
 * site, which reads as "this Slot has no default content".
 */
export function collectDefaultComponentIds(
  component: Component.ComponentType,
  slotFieldId: string
): Set<string> {
  const ids = new Set<string>();

  const defaultExpr = getSlotDefaultExpression(component, slotFieldId);
  if (defaultExpr) {
    // Only the top-level items are content of *this* slot. A component nested
    // deeper -- inside a default component's own slot override, say -- belongs
    // to that slot, and forcing it in here would widen the restriction beyond
    // what the author placed. (forEachSlotDefaultComponentNode walks at any
    // depth because it prunes dependencies; that is a different question.)
    for (const item of Expressions.getList(defaultExpr) ?? []) {
      const element = Expressions.getElement(item);
      if (!element || getPluginName(element.type) !== '__SitePlugin') continue;
      ids.add(`${getPluginName(element.type)}::${getName(element.type)}`);
    }
    return ids;
  }

  const fieldType = Types.getRecordFields(Component.getDataType(component))?.[
    slotFieldId
  ];
  const defaultValue = fieldType ? getSystem(fieldType)?.defaultValue : null;
  if (!Array.isArray(defaultValue)) return ids;

  // Top-level items only, matching the defaultData branch above.
  for (const item of defaultValue) {
    if (
      isNodePojo(item) &&
      item.pluginType === '__SitePlugin' &&
      typeof item.type === 'string'
    ) {
      ids.add(`${item.pluginType}::${item.type}`);
    }
  }
  return ids;
}

/**
 * Extracts override field identifier from a Slot element
 */
export function getSlotOverrideFieldId(
  slotContentElement: ExpressionElement
): string | null {
  const slotChildrenExpr = Expressions.getIn(
    Expressions.getElementData(slotContentElement),
    childrenPath
  );

  if (slotChildrenExpr) {
    const select = Expressions.getSelect(slotChildrenExpr);
    if (select && Expressions.isDataVariable(select.from)) {
      return select.prop;
    }
  }

  return null;
}

/**
 * Whether a Slot element's field has default content on its owner component.
 */
export function slotElementHasDefaults(
  plugins: Plugins.PluginsType,
  components: Component.ComponentMap,
  ownerComponentType: ExpressionElement['type'],
  slotElement: ExpressionElement
): boolean {
  const slotFieldId = getSlotOverrideFieldId(slotElement);
  if (!slotFieldId) return false;

  return (
    getSlotDefaultElements(plugins, components, ownerComponentType, slotFieldId)
      .length > 0
  );
}

/**
 * Whether a Slot element is being edited inside its own component definition. The
 * component the slot belongs to is the one currently focused.
 */
export function isSlotInFocusedComponent(
  plugins: Plugins.PluginsType,
  components: Component.ComponentMap,
  slotElement: ExpressionElement,
  selectedNativeIdPath: NativeId[],
  focusedComponentType: ExpressionElement['type']
): boolean {
  if (!isSlot(slotElement)) return false;

  const ownerInstance = getInnermostSymbolInstance(
    selectedNativeIdPath,
    components,
    plugins
  );
  if (!ownerInstance?.type) return false;

  return namesEqual(ownerInstance.type, focusedComponentType);
}

/**
 * Find the closest slot ancestor and corresponding component instance
 *
 * Finds the closest slot ancestor of the element corresponding to `elementId`
 * within `currentComponent`. Returns the slot, the component instance
 * that the slot belongs to, and the expression path to the slot override
 * in the component instance data. Returns null if element is not inside a slot.
 */
export function getClosestSlotInfo(
  elementId: string,
  currentComponent: Component.ComponentType,
  componentMap: Component.ComponentMap
): null | {
  componentInstance: ExpressionElement;
  slot: ExpressionElement;
  anchorPath: ExpressionPath;
} {
  // these include the element itself and are ordered from root to element
  const ancestors = getElementWithAncestors(
    Component.getRender(currentComponent)
  )(elementId);

  let componentInstance: ExpressionElement | undefined;
  let instanceChild: ExpressionElement | undefined;

  // we start the search from element and walk towards root
  for (let i = ancestors.length - 1; i > 0; i--) {
    // the element itself cannot be a instance candidate
    const componentInstanceCandidate = ancestors[i - 1];
    const instanceChildCandidate = ancestors[i];

    // if componentInstanceCandidate is a component instance,
    // then instanceChildCandidate must be an element located in
    // one of that component instance's overrides
    if (
      componentInstanceCandidate &&
      isUserComponentInst(componentMap, componentInstanceCandidate) &&
      instanceChildCandidate
    ) {
      componentInstance = componentInstanceCandidate;
      instanceChild = instanceChildCandidate;
      break;
    }
  }

  // if we have a´found a componentInstance and an instanceChild,
  // we still need check if the override of componentInstance that
  // instanceChild belongs to is of type SlotContent and has a
  // corresponding Slot element inside the component
  if (componentInstance && instanceChild) {
    const component = componentMap.get(componentInstance.type);
    const instanceChildId = instanceChild.id;

    // paths from componentInstance data to instanceChild
    const paths = Expressions.findPathsWithStoppable(
      Expressions.getElementData(componentInstance),
      matchExpression({
        // we know instanceChild is a direct child of componentInstance
        // so we can optimize a little by not continuing traversal
        // down to data of elements we encounter
        Element: (el) => Expressions.stop(el.id === instanceChildId),
        _: constant(Expressions.resume(false)),
      })
    );

    if (component && paths.length === 1 && paths[0]) {
      const path = paths[0];

      // try to extract a potential override id from the path
      // it should be the first record access in the expression path
      let maybeSlotOverrideId: string | undefined;
      for (let i = 0; i < path.length; i++) {
        const part = path[i];
        if (part?.in === 'Record') {
          maybeSlotOverrideId = part.at;
          break;
        }
      }

      if (maybeSlotOverrideId) {
        const slotPath: ExpressionPath = [
          {in: 'Record', at: maybeSlotOverrideId},
        ];
        const componentDataType = Component.getDataType(component);
        const propertyDataType = Types.__DEPRECATED__getTypeAtExpressionPath(
          componentDataType,
          slotPath
        );

        // does our override id candidate match an SlotContent override?
        if (propertyDataType && Types.isSlotContentType(propertyDataType)) {
          const slot = getComponentSlots(component).find(
            (slotCandidate) =>
              getSlotOverrideFieldId(slotCandidate) === maybeSlotOverrideId
          );

          // and does it have a corresponding slot?
          if (slot) {
            return {componentInstance, slot, anchorPath: slotPath};
          }
        }
      }
    }
  }

  return null;
}

export const isElementInsideSlot: (
  componentMap: Component.ComponentMap
) => (
  currentComponent: Component.ComponentType
) => (element: ExpressionElement) => boolean = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((currentComponent: Component.ComponentType) =>
      weakMemo((element: ExpressionElement): boolean => {
        return Boolean(
          getClosestSlotInfo(element.id, currentComponent, componentMap)
        );
      })
    )
);

/**
 * Get Slot matching `nativeIdPath` in `maybeComponentInstance`
 */
function getSlotInComponentInstance(
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  maybeComponentInstance: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionElement | null {
  const component = componentMap.get(maybeComponentInstance.type);

  if (
    !isUserComponentInst(componentMap, maybeComponentInstance) ||
    !component
  ) {
    // maybeComponentInstance is not a component instance
    return null;
  }

  const relativePath = getRelativePath(nativeIdPath, focusedSymbolNativeIdPath);
  if (!relativePath.length) return null;

  const targetInstance = getInnermostSymbolInstance(
    nativeIdPath,
    componentMap,
    plugins
  );
  if (!targetInstance || targetInstance.id !== maybeComponentInstance.id) {
    return null;
  }

  const targetSlotId = getGuidNullable(relativePath[relativePath.length - 1]);
  if (!targetSlotId) return null;

  return (
    getComponentSlots(component).find(
      (slotCandidate) => slotCandidate.id === targetSlotId
    ) ?? null
  );
}

/**
 * Check if `nativeIdPath` matches a Slot in `maybeComponentInstance`
 */
export function isPathTargetingSlotInComponentInstance(
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  maybeComponentInstance: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): boolean {
  return Boolean(
    getSlotInComponentInstance(
      nativeIdPath,
      focusedSymbolNativeIdPath,
      maybeComponentInstance,
      componentMap,
      plugins
    )
  );
}

/**
 * Determines the anchorPath to use if the targeted anchor is a slot
 */
export function getTargetedSlotAnchorPath(
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  anchorElement: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionPath | null {
  const anchorTopLevelSlot = getSlotInComponentInstance(
    nativeIdPath,
    focusedSymbolNativeIdPath,
    anchorElement,
    componentMap,
    plugins
  );

  const component = componentMap.get(anchorElement.type);

  if (anchorTopLevelSlot && component) {
    return getSlotAnchorPath(anchorTopLevelSlot, component);
  }

  return null;
}

// Which component declares each Slot. Built once per map: the drag loop asks
// this on every frame a Slot is under the cursor.
const slotOwnersByElementId = weakMemo(
  (
    componentMap: Component.ComponentMap
  ): ReadonlyMap<string, Component.ComponentType> => {
    const owners = new Map<string, Component.ComponentType>();
    componentMap.forEach((component) => {
      for (const slot of getComponentSlots(component)) {
        owners.set(slot.id, component);
      }
    });
    return owners;
  }
);

/**
 * The component that declares this Slot, wherever it lives in the project.
 */
export const getSlotOwnerComponent = (
  componentMap: Component.ComponentMap,
  slot: ExpressionElement
): Component.ComponentType | null =>
  slotOwnersByElementId(componentMap).get(slot.id) ?? null;

// Which component's Slot defaults hold each element. Built once per map, the
// same way slotOwnersByElementId is.
const slotDefaultOwnersByElementId = weakMemo(
  (
    componentMap: Component.ComponentMap
  ): ReadonlyMap<string, Component.ComponentType> => {
    const owners = new Map<string, Component.ComponentType>();
    componentMap.forEach((component) => {
      for (const slot of getComponentSlots(component)) {
        const fieldId = getSlotOverrideFieldId(slot);
        if (!fieldId) continue;
        const expression = getSlotDefaultExpression(component, fieldId);
        if (!expression) continue;
        for (const id of Object.keys(Expressions.allElements(expression))) {
          owners.set(id, component);
        }
      }
    });
    return owners;
  }
);

/**
 * The component whose Slot default holds this element, if any.
 *
 * A promoted placement resolves a Slot to the instance that owns it, so the
 * element handed to validation is no longer a Slot. Where it lives is the only
 * thing left that still says the drop is authoring a default.
 */
export const getSlotDefaultOwnerComponent = (
  componentMap: Component.ComponentMap,
  element: ExpressionElement
): Component.ComponentType | null =>
  slotDefaultOwnersByElementId(componentMap).get(element.id) ?? null;

/**
 * The SlotContent field a drop or paste will land in, for VALIDATION ONLY.
 *
 * Never feed this to placement. `getTargetedSlotAnchorPath` carries a contract
 * the placement code depends on: a non-null path names a field of
 * `anchorElement`'s OWN data. `addElementAtAnchorInPath` writes through
 * `sparseSetIn(getElementData(anchorElement), anchorPath, ...)`, and
 * `getPastePosition` reads `isUserComponentInst(anchorElement) && !anchorPath`
 * to choose between pasting beside the instance and pasting inside it.
 *
 * This function deliberately breaks that contract to answer a different
 * question: which slot is being filled? When the anchor is content already
 * sitting in a slot -- the shape you get dropping beside existing slot content
 * -- the answer is a field of the CONTAINING instance, which would corrupt
 * placement but is exactly what validateSlotRestrictions needs. Without it the
 * validator has no field to check and must refuse on any restriction the
 * component carries, rejecting legitimate drops into an unrestricted slot.
 */
export function resolveValidationSlotAnchorPath(
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  anchorElement: ExpressionElement,
  currentComponent: Component.ComponentType,
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): ExpressionPath | null {
  const targeted = getTargetedSlotAnchorPath(
    nativeIdPath,
    focusedSymbolNativeIdPath,
    anchorElement,
    componentMap,
    plugins
  );
  if (targeted) return targeted;

  return (
    getClosestSlotInfo(anchorElement.id, currentComponent, componentMap)
      ?.anchorPath ?? null
  );
}

/**
 * Determines the anchorPath to use for slot of component
 */
export function getSlotAnchorPath(
  slot: ExpressionElement,
  component: Component.ComponentType
): ExpressionPath | null {
  const slotOverrideId = getSlotOverrideFieldId(slot);
  if (slotOverrideId) {
    const slotPath: ExpressionPath = [{in: 'Record', at: slotOverrideId}];
    const propertyDataType = Types.__DEPRECATED__getTypeAtExpressionPath(
      Component.getDataType(component),
      slotPath
    );

    if (propertyDataType && Types.isSlotContentType(propertyDataType)) {
      return slotPath;
    }
  }
  return null;
}

/**
 * Resolve the SlotContent field a drop targets when authoring a component
 * definition's slot default. Returns null unless the drop lands inside one of
 * the component's own Slots (not a nested instance's slot).
 */
export function getSlotDefaultDropFieldId(
  component: Component.ComponentType,
  anchorElementId: string,
  position: RelativePositionPlacementOptions
): string | null {
  const render = Component.getRender(component);
  // Nearest Slot at or above the drop anchor (inclusive — the anchor itself can match).
  const slot = Expressions.closestElement(render, anchorElementId, isSlot);
  if (!slot) {
    return null;
  }

  // When the anchor is the Slot itself, only an into-position authors the
  // default; before/after would drop a sibling beside the Slot, not inside it.
  if (
    slot.id === anchorElementId &&
    position !== 'append' &&
    position !== 'prepend'
  ) {
    return null;
  }

  const slotPath = getSlotAnchorPath(slot, component);
  const first = slotPath?.[0];
  if (first && first.in === 'Record') {
    return first.at;
  }
  return null;
}

/**
 * Given an element with slots, a component, and plugins, removes the slot
 * fields from the data type and cleans up any references in the component render
 * that reference those removed fields.
 */
export const handleSlotFieldRemoval = (
  element: ExpressionElement,
  component: Component.ComponentType,
  plugins: Plugins.PluginsType
): Component.ComponentType => {
  const dataType = Component.getDataType(component);
  const slots = getSlots(element);

  if (slots.length === 0) {
    return component;
  }

  const originalTypeFields = Types.getRecordFields(dataType);

  if (!originalTypeFields) {
    return component;
  }

  let typeFields = originalTypeFields;
  let render = Component.getRender(component);
  let defaultData = Component.getDefaultData(component);

  for (const slotElement of slots) {
    const overrideIdToRemove = getSlotOverrideFieldId(slotElement);
    if (overrideIdToRemove && overrideIdToRemove in typeFields) {
      const {[overrideIdToRemove]: _, ...newTypeFields} = typeFields;
      typeFields = newTypeFields;
      render = unbindReferences(render, overrideIdToRemove, component, plugins);
      if (defaultData) {
        defaultData = Expressions.deleteInRecord(defaultData, [
          {in: 'Function'},
          {in: 'Record', at: overrideIdToRemove},
        ]);
      }
    }
  }

  if (typeFields === originalTypeFields) {
    return component;
  }

  return Component.update(component, {
    dataType: TRecord(typeFields),
    render,
    defaultData,
  });
};

/**
 * Given a component instance and a slot element,
 * return the slot's override elements
 */
export function getSlotOverrideElements(
  slotElement: ExpressionElement,
  componentInstance: ExpressionElement
): ExpressionElement[] {
  const slotOverrideId = getSlotOverrideFieldId(slotElement);
  if (!slotOverrideId) return emptyArray;

  const slotOverrideExpr = Expressions.getIn(
    Expressions.getElementData(componentInstance),
    [{in: 'Record', at: slotOverrideId}]
  );
  if (!slotOverrideExpr) return emptyArray;

  return getDescendantsFromExpression(slotOverrideExpr);
}

/**
 * Resets the Slot override property if the element that is removed from the slot is the only child of the slot.
 *
 * An absent override field means use the default, while a field holding an empty list means this
 * instance is deliberately empty. So when the slot field declares a `defaultValue` the emptied
 * override is kept as an explicit empty list, otherwise the key is deleted. Without that
 * distinction, emptying a slot would render its default content again.
 *
 * `componentMap` and `plugins` are what let us read the `defaultValue` off the owning component's
 * dataType.
 *
 * @returns The component instance with the slot override property reset if it is empty. If the slot is not empty, returns the component instance as is.
 */
export function resetSlotPropertyIfEmpty(
  elementToBeRemovedFromSlot: ExpressionElement,
  componentInstance: ExpressionElement,
  slotElement: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ExpressionElement {
  const slotOverrideId = getSlotOverrideFieldId(slotElement);
  if (slotOverrideId) {
    const slotOverridePath: ExpressionPath = [
      {in: 'Record', at: slotOverrideId},
    ];
    const slotChildrenExpr = Expressions.getIn(
      Expressions.getElementData(componentInstance),
      slotOverridePath
    );

    const slotChildren = slotChildrenExpr
      ? getDescendantsFromExpression(slotChildrenExpr)
      : [];
    const removesOnlyChild =
      slotChildren.length === 1 &&
      Boolean(slotChildren[0]) &&
      slotChildren[0]?.id === elementToBeRemovedFromSlot.id;

    if (slotChildren.length === 0 || removesOnlyChild) {
      const elementData = Expressions.getElementData(componentInstance);
      const hasSlotDefault = slotElementHasDefaults(
        plugins,
        componentMap,
        componentInstance.type,
        slotElement
      );

      return Expressions.setElementData(
        componentInstance,
        removesOnlyChild && hasSlotDefault
          ? Expressions.setIn(elementData, slotOverridePath, EList([]))
          : Expressions.deleteInRecord(elementData, slotOverridePath)
      );
    }
  }
  return componentInstance;
}

/**
 * Given a field id & a component instance's render expression,
 * return the slot element that contains the field
 */
export function getSlotFromOverrideFieldId(
  componentInstExpr: Expression,
  fieldId: string
): ExpressionElement | null {
  const element = Expressions.getElement(componentInstExpr);
  if (!element) return null;
  const slots = getSlots(element);
  return slots.find((slot) => getSlotOverrideFieldId(slot) === fieldId) ?? null;
}

/**
 * The element a canvas interaction targets, when the path may run through slot
 * default content.
 *
 * Three cases, in order. Inside the owner's definition the DOM lookup lands on
 * the Slot that renders the default, because the default's own elements are in
 * no render — there the component the default places is the target. Otherwise a
 * live render hit is the target. Finally, when the current component's own
 * default content is named, it is read off `defaultData`.
 */
export function resolveSlotDefaultAwareTargetElement({
  plugins,
  components,
  currentComponentType,
  currentComponentRender,
  nativeIdPath,
  elementId,
}: {
  plugins: Plugins.PluginsType;
  components: Component.ComponentMap;
  currentComponentType: ExpressionElement['type'];
  currentComponentRender: Expression;
  nativeIdPath: NativeId[];
  elementId: string | null;
}): ExpressionElement | null {
  const elementFromRender = elementId
    ? Expressions.getElementById(currentComponentRender, elementId)
    : null;

  const isSlotDefaultTarget = Boolean(
    resolveSlotDefaultPathContext(nativeIdPath, components, plugins)
  );

  if (isSlotDefaultTarget && elementFromRender && isSlot(elementFromRender)) {
    return getInnermostSymbolInstance(nativeIdPath, components, plugins);
  }

  if (elementFromRender) return elementFromRender;

  return elementId
    ? (findSlotDefaultElement(
        plugins,
        components,
        currentComponentType,
        elementId
      )?.element ?? null)
    : null;
}

/**
 * Projects an owner's slot default content into a render expression, so an
 * element that only exists on the owner's `defaultData` can be found by id in
 * the tree the Designer resolves ids against.
 *
 * A slot override needs nothing like this: it is authored on the instance's own
 * data, so it is already part of the expression. A default is not, and renders
 * nested inside the owner's Slot, so that is where it is projected.
 *
 * Returns null when `elementId` is not default content of that owner, or when
 * the owner's render has no Slot for the field holding it.
 */
export function projectSlotDefaultIntoRender(
  plugins: Plugins.PluginsType | null,
  components: Component.ComponentMap,
  expression: Expression,
  ownerComponentType: ExpressionElement['type'],
  elementId: string,
  ownerInstance?: ExpressionElement | null
): {expression: Expression; element: ExpressionElement} | null {
  const hit = findSlotDefaultElementAcrossTrees(
    plugins,
    components,
    ownerComponentType,
    elementId,
    ownerInstance
  );
  if (!hit) return null;

  const ownerComponent = components.get(ownerComponentType);
  const slotElement = ownerComponent
    ? getSlotFromOverrideFieldId(
        Component.getRender(ownerComponent),
        hit.slotFieldId
      )
    : null;
  if (!slotElement) return null;

  return {
    element: hit.element,
    // The Slot's `children` selects the instance's override field, which
    // resolves to the projected content only at render time.
    expression: Expressions.updateElement(
      slotElement.id,
      (slot) =>
        Expressions.setElementData(
          slot,
          Expressions.sparseUpdateIn(
            Expressions.getElementData(slot),
            childrenPath,
            () => EList(hit.defaultElements.map((element) => EElement(element)))
          )
        ),
      expression
    ),
  };
}

/**
 * Given a slot element, return the slot's name
 */
export function getSlotName(slotElement: ExpressionElement): string | null {
  const expr = Expressions.getElementData(slotElement);
  const name = Expressions.getIn(expr, [{in: 'Record', at: 'name'}]);
  return name ? Expressions.getText(name) : null;
}

/**
 * Resolves a label for a slot binding. If the fieldId is not a slot content field, returns null.
 */
export function resolveSlotBindingLabel(
  currentComponent: Component.ComponentType,
  fieldId: string | null,
  styleBlockState: StyleBlockStateType
): string | null {
  if (!fieldId) {
    return null;
  }
  const fields = Types.getRecordFields(currentComponent.dataType);
  const fieldType = fields?.[fieldId];
  if (!fieldType || !Types.isSlotContentType(fieldType)) {
    return null;
  }

  return (
    resolveSlotLabel(
      currentComponent.render,
      styleBlockState,
      fieldId,
      fieldType
    ) || null
  );
}

/**
 * Resolves a label for a slot based on the following priority:
 * 1. The custom display name from slot metadata (if available)
 * 2. The name of the slot (if available)
 * 3. The name of the first associated style block (if available)
 * 4. The property name from the type's label (fallback)
 */
export const resolveSlotLabel = (
  renderExpr: Expression,
  styleBlockState: StyleBlockStateType,
  key: string,
  currentType: Type
): string => {
  const typeLabel = Types.getLabel(currentType);
  const {groupName, propertyName: defaultPropertyName} =
    parsePropertyLabel(typeLabel);

  const formatLabel = (name: string) =>
    groupName ? createPropertyLabel({groupName, propertyName: name}) : name;

  const slot = getSlotFromOverrideFieldId(renderExpr, key);
  if (!slot) return formatLabel(defaultPropertyName);

  // Priority 1: Custom display name from metadata
  const customSlotName = slot.meta?.displayName;
  if (typeof customSlotName === 'string' && customSlotName.length > 0) {
    return formatLabel(customSlotName);
  }

  // Priority 2: Slot name
  const slotName = getSlotName(slot);
  if (slotName) return formatLabel(slotName);

  // Priority 3: First style block name
  const [firstStyleBlockId] = getStyleBlockIds(slot);
  if (firstStyleBlockId) {
    const blockName = styleBlockState.getBlock(firstStyleBlockId)?.get('name');
    if (blockName) return formatLabel(blockName);
  }

  // Priority 4: Fallback to default property name
  return formatLabel(defaultPropertyName);
};

/**
 * Detects if the given user component instance has a cyclic slot ancestor, i.e.
 * if the component instance is inside a slot of an instance of the same component
 * either directly or indirectly.
 */
export const componentInstanceHasCyclicSlotAncestor = weakMemo(
  (nativeIdPath: NativeId[]) =>
    weakMemo((componentMap: Component.ComponentMap): boolean => {
      const instance = getInnermostSymbolInstance(nativeIdPath, componentMap);

      if (!instance) {
        return false;
      }

      const stack: Array<{
        element: ExpressionElement;
        hostComponent: Component.ComponentType;
      }> = [];

      visitInstancesInNativeIdPath(
        nativeIdPath,
        componentMap,
        (componentInstance, hostComponent) => {
          stack.push({element: componentInstance, hostComponent});
        }
      );

      while (stack.length) {
        const stackEntry = stack.pop();

        if (!stackEntry) {
          // This should never happen but as the `pop` returns undefined
          // on an empty array we need to check it here to pass type check.
          continue;
        }

        const {element, hostComponent} = stackEntry;

        const slotInfo = getClosestSlotInfo(
          element.id,
          hostComponent,
          componentMap
        );

        if (!slotInfo) {
          continue;
        }

        const {slot, componentInstance: instanceWithSlot} = slotInfo;

        // If the component instance in which the surrounding slot exists has the same type
        // as the instance passed into this function as an argument, it means there is a cyclic
        // slot ancestor and we can return early.
        if (
          isUserComponentInst(componentMap, instanceWithSlot) &&
          namesEqual(instanceWithSlot.type, instance.type)
        ) {
          return true;
        }

        // We push the instance in which the surrounding slot exists into the stack because
        // we also need to determine if it is inside a slot of a cyclic slot ancestor.
        stack.push({element: instanceWithSlot, hostComponent});

        // We read the slot override ID from the surrounding slot so that we can find the
        // matching Slot element in the instance's component root.
        const slotOverrideId = getSlotOverrideFieldId(slot);

        if (!slotOverrideId) {
          continue;
        }

        // Slot host component is the component in which the Slot element
        // matching the slot override ID exists.
        const slotHostComponent = componentMap.get(instanceWithSlot.type);

        if (!slotHostComponent) {
          continue;
        }

        const slotHostComponentRoot = Component.getRender(slotHostComponent);

        if (!slotHostComponentRoot) {
          continue;
        }

        // We find the Slot element matching the slot override ID from the
        // slot host component root and push it to the stack because we need
        // to also determine if the Slot element is inside another slot of
        // another component instance.
        const slotInSlotHostRoot = getSlotFromOverrideFieldId(
          slotHostComponentRoot,
          slotOverrideId
        );

        if (slotInSlotHostRoot) {
          stack.push({
            element: slotInSlotHostRoot,
            hostComponent: slotHostComponent,
          });
        }
      }

      // If we're here it means we did not find a cyclic slot ancestor,
      // so we can return false.
      return false;
    })
);

/**
 * Convenience function to iterate over a given nativeIdPath with
 * a visitor that has two arguments: 1) the user component instance matching the
 * native ID in the path, and 2) the "host component", i.e. the component in
 * which the user component instance (in the first arg) exists.
 */
function visitInstancesInNativeIdPath(
  nativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  visitor: (
    componentInstance: ExpressionElement,
    hostComponent: Component.ComponentType
  ) => void
): void {
  let hostComponent: Component.ComponentType | undefined =
    getPageComponentFromComponentMap(componentMap);

  for (const nativeId of nativeIdPath) {
    const guid = getGuidNullable(nativeId);

    if (!guid || !hostComponent) {
      // Something is wrong if either of these was not found.
      // Breaking out from this loop as we cannot continue with
      // corrupt data.
      break;
    }

    const hostComponentRoot = Component.getRender(hostComponent);

    const maybeComponentInstance = Expressions.getElementById(
      hostComponentRoot,
      guid
    );

    if (
      maybeComponentInstance &&
      isUserComponentInst(componentMap, maybeComponentInstance)
    ) {
      visitor(maybeComponentInstance, hostComponent);
      hostComponent = componentMap.get(maybeComponentInstance.type);
    }
  }
}

/**
 * Slot elements of a component, keyed by the SlotContent field each one binds.
 *
 * Memoized on the component: resolving a restriction otherwise walks the whole
 * render, and validateSlotRestrictions asks once per SlotContent field on the
 * component, on every drop it validates.
 */
const getSlotsByFieldId = weakMemo((component: Component.ComponentType) => {
  const byFieldId = new Map<string, ExpressionElement>();
  for (const slot of getComponentSlots(component)) {
    const fieldId = getSlotOverrideFieldId(slot);
    if (fieldId && !byFieldId.has(fieldId)) byFieldId.set(fieldId, slot);
  }
  return byFieldId;
});

/**
 * Every Slot element id in the site, mapped to the component whose render
 * defines it.
 *
 * Slot element ids are stable within their defining component, so this resolves
 * a Slot's owner without needing the Slot to be reachable from whatever render
 * the caller happens to be looking at -- which it is not when a Marketer
 * selects a Slot inside an instance and selection stays page level.
 */
export const getComponentBySlotElementId = weakMemo(
  (componentMap: Component.ComponentMap) => {
    const bySlotElementId = new Map<string, Component.ComponentType>();
    componentMap.forEach((component) => {
      for (const slot of getComponentSlots(component)) {
        if (!bySlotElementId.has(slot.id)) {
          bySlotElementId.set(slot.id, component);
        }
      }
    });
    return bySlotElementId;
  }
);

/** Every component in the map, keyed by its `plugin::name` id. */
const getComponentsByIdInMap = weakMemo(
  (componentMap: Component.ComponentMap) => {
    const components = new Map<string, Component.ComponentType>();
    componentMap.forEach((component, name) => {
      components.set(`${getPluginName(name)}::${getName(name)}`, component);
    });
    return components;
  }
);

/** Every component id present in the map, in `plugin::name` form. */
const getComponentIdsInMap = weakMemo(
  (componentMap: Component.ComponentMap) =>
    new Set(getComponentsByIdInMap(componentMap).keys())
);

/** The plugin a site's own components are qualified under. */
const SITE_PLUGIN_NAME = '__SitePlugin';

/**
 * The component a `plugin::name` id names, or undefined when the map has none.
 * Keeps the id format in this file rather than having callers parse it.
 */
export function getComponentForId(
  componentId: string,
  componentMap: Component.ComponentMap
): Component.ComponentType | undefined {
  return getComponentsByIdInMap(componentMap).get(componentId);
}

/**
 * Each library-derived component's two names, mapped two ways: `aliases` links
 * the `__SitePlugin::<guid>` of its local copy and the `<libraryId>::<name>` it
 * was published under in both directions, so a stored id in either spelling can
 * reach the other; `originToLocal` is the origin-to-local half alone, for
 * callers that need to know which spelling is the local one.
 */
const getComponentIdMaps = weakMemo((componentMap: Component.ComponentMap) => {
  const aliases = new Map<string, string>();
  const originToLocal = new Map<string, string>();
  const claimedOrigins = new Set<string>();

  componentMap.forEach((component, name) => {
    // Only a site-authored component can be the local copy of something.
    if (!isUserComponent(name)) return;
    const origin = Component.getOrigin(component);
    // Some user components carry a stray Basic::* origin left over from a
    // migration bug (SCALE-959); without this guard one would claim that
    // origin key away from the library entry that owns it.
    if (!origin || getPluginName(origin) === 'Basic') return;
    const localId = `${name[0]}::${name[1]}`;
    const originId = `${origin[0]}::${origin[1]}`;

    // A local id has exactly one origin, so this direction is never
    // ambiguous.
    aliases.set(localId, originId);

    // The reverse can be: two local copies claiming one origin leave no
    // answer to "which copy does this origin name". Resolving it to
    // whichever the map iterated last would let enforcement accept, and the
    // panel show, the copy that was not allow-listed. Dropping the key makes
    // canonicalComponentId fall back to the id it was given.
    if (claimedOrigins.has(originId)) {
      aliases.delete(originId);
      originToLocal.delete(originId);
      return;
    }
    claimedOrigins.add(originId);
    aliases.set(originId, localId);
    originToLocal.set(originId, localId);
  });

  return {aliases, originToLocal};
});

/**
 * Library-derived components keyed by the component name half of their id,
 * mapped to the id an allow-list should store. `null` marks a name that more
 * than one component answers to.
 *
 * A design library's name half is a GUID that survives republishes, while the
 * plugin half (the versioned resource id) changes each time. Keying by name
 * lets a stored id outlive a republish.
 *
 * Every plugin-qualified entry is indexed because the map can't tell an
 * installed library from a core plugin. Core plugins never collide (their
 * names aren't GUIDs). Code libraries use slugified names (e.g. `button`), so
 * two exporting the same name collide. That is only caught while both are
 * installed; if the stored one is removed, the remaining one resolves.
 */
const getLibraryIdsByComponentName = weakMemo(
  (componentMap: Component.ComponentMap) => {
    const byName = new Map<string, string | null>();

    componentMap.forEach((_component, name) => {
      // Skips everything under the site plugin, including the page
      // pseudo-component, which isUserComponent alone wouldn't exclude.
      if (getPluginName(name) === SITE_PLUGIN_NAME) return;
      const seen = byName.has(name[1]);
      byName.set(name[1], seen ? null : `${name[0]}::${name[1]}`);
    });

    // A local copy wins over the library entry for the same name, unless the
    // name is ambiguous: the stored id holds a retired version id, so it can't
    // say which library the author picked, and the copy only reflects what the
    // site happened to materialize.
    //
    // Collected separately so two copies of one origin are ambiguous too.
    const localIdsByOriginName = new Map<
      string,
      {localId: string; originPlugin: string} | null
    >();
    componentMap.forEach((component, name) => {
      // Same guards as the origin maps above: only site-authored components
      // are local copies.
      if (!isUserComponent(name)) return;
      const origin = Component.getOrigin(component);
      if (!origin || getPluginName(origin) === 'Basic') return;
      const localId = `${name[0]}::${name[1]}`;
      localIdsByOriginName.set(
        origin[1],
        localIdsByOriginName.has(origin[1])
          ? null
          : {localId, originPlugin: origin[0]}
      );
    });

    localIdsByOriginName.forEach((local, componentName) => {
      if (local === null) {
        byName.set(componentName, null);
        return;
      }
      // Already ambiguous among libraries; leave it unresolved.
      const libraryId = byName.get(componentName);
      if (libraryId === null) return;

      // A copy from a different plugin than the library entry is the same
      // ambiguity, so refuse rather than let the copy win.
      if (
        libraryId &&
        libraryId !== `${local.originPlugin}::${componentName}`
      ) {
        byName.set(componentName, null);
        return;
      }
      byName.set(componentName, local.localId);
    });

    return byName;
  }
);

/**
 * The id an allow-list should store for a component: the local
 * `__SitePlugin::<guid>` copy once one exists, otherwise the
 * `<libraryId>::<name>` it is published under.
 */
export function canonicalComponentId(
  componentId: string,
  componentMap: Component.ComponentMap
): string {
  return (
    getComponentIdMaps(componentMap).originToLocal.get(componentId) ??
    componentId
  );
}

/**
 * The id an allow-list should store for whatever `componentId` refers to, or
 * undefined when nothing in the map answers to it.
 *
 * An id is live if:
 *
 * 1. It names a component in the map outright.
 * 2. Its alias does: a library-qualified id whose component is present as a
 *    local copy.
 * 3. Its name half does. The plugin half of a library-qualified id changes on
 *    every republish, but the name half (a GUID) survives.
 *
 * Step 3 can be ambiguous when two installed libraries hold a component under
 * one GUID. It then refuses to pick, leaving the entry unresolved, since
 * allowing a component the author never chose is worse.
 */
export function resolveAllowedComponentId(
  componentId: string,
  componentMap: Component.ComponentMap
): string | undefined {
  const ids = getComponentIdsInMap(componentMap);
  if (ids.has(componentId)) {
    // Canonicalizing is itself library-aware -- it rewrites a
    // library-qualified id to its local copy's.
    return canonicalComponentId(componentId, componentMap);
  }

  const alias = getComponentIdMaps(componentMap).aliases.get(componentId);
  if (alias && ids.has(alias)) {
    return canonicalComponentId(alias, componentMap);
  }

  const separator = componentId.indexOf('::');
  if (separator < 0) return undefined;
  const pluginName = componentId.slice(0, separator);
  const componentName = componentId.slice(separator + 2);
  // A __SitePlugin name half is the component's own id, so a name match adds
  // nothing, and a deleted local component must not resolve to a same-named
  // stranger.
  if (pluginName === SITE_PLUGIN_NAME || !componentName) return undefined;

  return (
    getLibraryIdsByComponentName(componentMap).get(componentName) ?? undefined
  );
}

/**
 * resolveAllowedComponentId over a collection, dropping what no longer
 * resolves and deduplicating what remains.
 */
export function resolveAllowedComponentIds(
  componentIds: Iterable<string>,
  componentMap: Component.ComponentMap
): Set<string> {
  const resolved = new Set<string>();
  for (const id of componentIds) {
    const next = resolveAllowedComponentId(id, componentMap);
    if (next) resolved.add(next);
  }
  return resolved;
}

/**
 * Drops ids that name no component in the map, collapses the rest to the id an
 * allow-list should store, and deduplicates. Order is preserved so a caller
 * that shows the list keeps the author's ordering.
 */
function dropUnresolvableIds(
  componentIds: Iterable<string>,
  componentMap: Component.ComponentMap
): string[] {
  // Set iteration is insertion order, so spreading keeps the author's
  // ordering.
  return [...resolveAllowedComponentIds(componentIds, componentMap)];
}

/**
 * Resolves slot restrictions for a given SlotContent field on a component.
 * Returns null when the slot is unrestricted (empty or absent allowedComponentIds).
 */
export function getSlotRestrictions(
  component: Component.ComponentType,
  slotFieldId: string,
  componentMap: Component.ComponentMap
): {allowedComponentIds: string[]; defaultComponentIds: Set<string>} | null {
  const slot = getSlotsByFieldId(component).get(slotFieldId);
  if (!slot) return null;

  const storedIds = getSlotAllowedComponentIds(slot);
  if (storedIds.length === 0) return null;

  // Nothing prunes an id when the component it names goes away: deleting a
  // component leaves its id behind in every allow-list naming it. Filtering to
  // an empty list if only unresolvable ids are present will unrestrict the slot.
  const allowedComponentIds = dropUnresolvableIds(storedIds, componentMap);
  if (allowedComponentIds.length === 0) return null;

  const defaultComponentIds = new Set(
    dropUnresolvableIds(
      collectDefaultComponentIds(component, slotFieldId),
      componentMap
    )
  );
  return {allowedComponentIds, defaultComponentIds};
}

// Expands allow-list ids to include each id's other spelling
export function withComponentIdAliases(
  componentIds: Iterable<string>,
  componentMap: Component.ComponentMap
): Set<string> {
  const {aliases} = getComponentIdMaps(componentMap);
  const expanded = new Set<string>();
  for (const id of componentIds) {
    expanded.add(id);
    const alias = aliases.get(id);
    if (alias) {
      expanded.add(alias);
    }
  }
  return expanded;
}

/**
 * Whether a component ID is allowed in a restricted slot — true if the ID is
 * in allowedComponentIds or in defaultComponentIds (defaults are always allowed).
 */
export function isComponentAllowedInSlot(
  restrictions: {
    allowedComponentIds: string[];
    defaultComponentIds: Set<string>;
  },
  componentId: string,
  componentMap?: Component.ComponentMap
): boolean {
  if (!componentMap) {
    return (
      restrictions.allowedComponentIds.includes(componentId) ||
      restrictions.defaultComponentIds.has(componentId)
    );
  }

  return (
    withComponentIdAliases(restrictions.allowedComponentIds, componentMap).has(
      componentId
    ) ||
    withComponentIdAliases(restrictions.defaultComponentIds, componentMap).has(
      componentId
    )
  );
}

export function isSlotInComponentInstance(
  nativeIdPath: NativeId[],
  focusedSymbolNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap,
  plugins?: Plugins.PluginsType
): boolean {
  if (
    !isPathToInstanceDescendant(
      nativeIdPath,
      componentMap,
      focusedSymbolNativeIdPath
    )
  ) {
    return false;
  }

  const targetElement = getTargetElementOrInstance(
    nativeIdPath,
    componentMap,
    plugins
  );
  return Boolean(targetElement && isSlot(targetElement));
}

/**
 * Whether `instance` overrides `slotFieldId` rather than inheriting its default.
 *
 * Reads the same `isDefault` flag the settings panel does, so the canvas offers
 * the reset exactly when the panel shows the slot prop as changed.
 */
const overridesSlotField = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  instance: ExpressionElement,
  slotFieldId: string
): boolean => {
  const field = Types.getRecordFields(
    getDataTypeFromElement(plugins)(componentMap)(instance)
  )?.[slotFieldId];

  return field ? getSystem(field)?.isDefault === false : false;
};

const resettableSlotTarget = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  instance: ExpressionElement,
  slotFieldId: string | null
) => {
  if (!slotFieldId) return null;

  // Resetting is defined against the default, so it means nothing on a slot
  // that has none.
  if (
    getSlotDefaultElements(plugins, componentMap, instance.type, slotFieldId)
      .length === 0
  ) {
    return null;
  }

  if (!overridesSlotField(plugins, componentMap, instance, slotFieldId)) {
    return null;
  }

  return {instanceId: instance.id, slotFieldId};
};

/**
 * The slot that resetting acts on for `selectedNativeIdPath`, or null when there
 * is nothing to reset, meaning the selection is not in a slot, the slot has no
 * default, or the instance is already inheriting that default.
 *
 * The selection can be the Slot itself, which is all an emptied slot leaves to
 * click, or content the instance placed in the slot. Either way the write needs
 * the owning instance and the SlotContent field on it.
 */
export function getSlotResetTarget(
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  currentComponent: Component.ComponentType,
  focusedSymbolNativeIdPath: NativeId[],
  selectedNativeIdPath: NativeId[]
): null | {instanceId: string; slotFieldId: string} {
  if (!selectedNativeIdPath.length) return null;

  const instance = getInnermostSymbolInstance(
    selectedNativeIdPath,
    componentMap,
    plugins
  );

  // The Slot itself is selected, and its anchor path names the field it binds.
  if (instance) {
    const slotAnchorPath = getTargetedSlotAnchorPath(
      selectedNativeIdPath,
      focusedSymbolNativeIdPath,
      instance,
      componentMap,
      plugins
    );
    if (slotAnchorPath) {
      return resettableSlotTarget(
        plugins,
        componentMap,
        instance,
        getTargetedSlotFieldId(slotAnchorPath)
      );
    }
  }

  // Otherwise the selection is content in a slot. Collapsing the path to the id
  // the rest of the Designer treats as selected is what lets a click deeper
  // inside an instance in the slot resolve back to that instance.
  const elementId = getGuidNullable(
    deriveNativeIdFromPath(selectedNativeIdPath, focusedSymbolNativeIdPath)
  );
  if (!elementId) return null;

  const slotInfo = getClosestSlotInfo(
    elementId,
    currentComponent,
    componentMap
  );
  if (!slotInfo) return null;

  return resettableSlotTarget(
    plugins,
    componentMap,
    slotInfo.componentInstance,
    getTargetedSlotFieldId(slotInfo.anchorPath)
  );
}
